import {Box, Octicon, Timeline} from '@primer/react'
import type {EventActor$key} from './__generated__/EventActor.graphql'
import {Ago} from './Ago'
import {EventActor} from './EventActor'
import type {Icon} from '@primer/octicons-react'
import {useSlots} from '@primer/react/experimental'
import type React from 'react'

type TimelineRowType = {
  actor: EventActor$key | null
  highlighted: boolean
  createdAt: string
  deepLinkUrl: string
  showAgoTimestamp?: boolean
  showActorName?: boolean
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
  children: React.ReactNode
  leadingIcon: Icon
  iconColoring?: {
    color?: string
    backgroundColor?: string
  }
  leadingIconOnTop?: boolean
}

const Row = ({
  actor,
  showActorName = true,
  showAgoTimestamp = true,
  highlighted,
  children,
  createdAt,
  deepLinkUrl,
  refAttribute,
  leadingIcon,
  leadingIconOnTop,
  iconColoring,
}: TimelineRowType) => {
  const [slots] = useSlots(children, {
    trailing: Trailing,
    secondary: Secondary,
    main: Main,
  })

  const highlightedStyle = highlighted
    ? {
        borderRadius: '50%',
        boxShadow: `0px 0px 0px 2px var(--fgColor-accent, var(--color-accent-fg)), 0px 0px 0px 4px var(--bgColor-accent-muted, var(--color-accent-subtle))`,
      }
    : {}

  return (
    <Timeline.Item
      sx={{
        py: 2,
      }}
      ref={highlighted ? refAttribute : null}
    >
      <Timeline.Badge
        sx={{
          backgroundColor: iconColoring?.backgroundColor,
          ...highlightedStyle,
        }}
      >
        {leadingIcon && (
          <Octicon
            icon={leadingIcon}
            sx={{
              color: iconColoring?.color,
              fontSize: '14px',
            }}
          />
        )}
      </Timeline.Badge>
      <Timeline.Body
        sx={{
          color: 'fg.subtle',
          fontSize: '14px',
          width: '100%',
          paddingRight: 5,
          display: 'flex',
          flexDirection: 'column',
          pl: 2,
        }}
      >
        <Box sx={{display: 'flex'}}>
          <Box sx={{flexGrow: 1, flexWrap: 'nowrap'}}>
            {showActorName && (
              <EventActor leadingIconOnTop={leadingIconOnTop} actor={actor} showAvatarOnly={!showActorName} />
            )}
            {slots.main}
            {showAgoTimestamp && <Ago timestamp={new Date(createdAt)} linkUrl={deepLinkUrl} />}
          </Box>
          <Box sx={{fontSize: 0}}>{slots.trailing}</Box>
        </Box>
        <Box>{slots.secondary}</Box>
      </Timeline.Body>
    </Timeline.Item>
  )
}

function Trailing(props: React.PropsWithChildren) {
  return <Box data-trailing>{props.children}</Box>
}

function Secondary(props: React.PropsWithChildren) {
  return <Box data-secondary>{props.children}</Box>
}

function Main(props: React.PropsWithChildren) {
  return <>{props.children}</>
}

export const TimelineRow = Object.assign(Row, {
  Trailing,
  Secondary,
  Main,
})

try{ Row.displayName ||= 'Row' } catch {}
try{ Trailing.displayName ||= 'Trailing' } catch {}
try{ Secondary.displayName ||= 'Secondary' } catch {}
try{ Main.displayName ||= 'Main' } catch {}
try{ TimelineRow.displayName ||= 'TimelineRow' } catch {}